// Small pieces shared by the demo page and the header persona menu.
import { ErrorBanner } from '@/components/evidence'
import { Badge, Banner, Button } from '@/components/ui'
import type { DemoPersona } from '@/domain/types'
import { fmtDate } from '@/lib/format'
import { credentialLines, demoErrorHint, errorCode, errorMessage } from './personas'
import { useCopy } from './useDemo'

/** Shows a demo-session failure: server errors with their code, other errors in plain words. */
export function DemoError({ error, title }: { error: unknown; title: string }) {
  if (!error) return null
  const code = errorCode(error)
  const message = errorMessage(error)
  const hint = demoErrorHint(code, message)
  if (code && code.startsWith('PW')) {
    return (
      <div className="mt-3 space-y-2">
        <ErrorBanner error={error} title={title} />
        {hint ? <p className="text-sm text-ink">{hint}</p> : null}
      </div>
    )
  }
  return (
    <Banner tone="bad" title={title} className="mt-3">
      <p>{hint ?? (message || 'Something went wrong. Try again.')}</p>
      {hint && message ? <p className="code mt-1 text-xs text-ink-faint">{message}</p> : null}
    </Banner>
  )
}

/** A persona's synthetic credentials: role, kind, stated expiry and revocation. */
export function CredentialList({ persona, today }: { persona: Pick<DemoPersona, 'credentials'>; today: string }) {
  const lines = credentialLines(persona, today)
  if (lines.length === 0) return <p className="text-sm text-ink-soft">No licence or certificate.</p>
  return (
    <ul className="space-y-1.5">
      {lines.map((c, i) => (
        <li key={`${c.role}-${i}`} className="text-sm">
          <span className="font-semibold text-ink">
            {c.roleLabel} ({c.role})
          </span>
          <span className="text-ink-soft">
            {' '}
            - {c.kind}, {c.expiresOn ? `expires ${fmtDate(c.expiresOn)}` : 'no stated expiry'}
          </span>
          {c.revoked || c.expiryPassed ? (
            <span className="ml-1 inline-flex flex-wrap gap-1 align-middle">
              {c.revoked ? <Badge tone="bad">Revoked</Badge> : null}
              {c.expiryPassed ? <Badge tone="bad">Expiry date passed</Badge> : null}
            </span>
          ) : null}
        </li>
      ))}
    </ul>
  )
}

/** Copy-to-clipboard button with a spoken confirmation. */
export function CopyButton({ text, label }: { text: string; label: string }) {
  const [state, copy] = useCopy()
  return (
    <span className="inline-flex flex-wrap items-center gap-2">
      <Button type="button" variant="secondary" onClick={() => copy(text)}>
        {label}
      </Button>
      <span aria-live="polite" className="text-xs font-semibold text-ink-soft">
        {state === 'copied' ? 'Copied' : state === 'failed' ? 'Copy did not work. Select the text and copy it by hand.' : ''}
      </span>
    </span>
  )
}
